import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { failureMessageConstant } from "../../../constants";

const SubConsent = ({
  subConsentDetails,
  setSubConsentDetails,
  toggleVisibility,
  onChange
}) => {
  const initialSubConsent = {
    _id: Date.now().toString(),
    name: "",
    isActive: false,
    isDeleted: false,
    addedOn: Date.now(),
    updatedOn: Date.now(),
    businessId: "",
    consentId: ""
  };

  // State to track validation errors
  const [errors, setErrors] = useState([]);

  // Ensure at least one input field is present if subConsents is empty
  useEffect(() => {
    if (subConsentDetails.length === 0) {
      setSubConsentDetails([initialSubConsent]);
    }
  }, [subConsentDetails, setSubConsentDetails]);

  const handleAddField = (fieldIndex) => {
    const newFields = [...subConsentDetails];
    newFields.splice(fieldIndex + 1, 0, initialSubConsent);
    setSubConsentDetails(newFields);
  };

  const handleRemoveField = (fieldIndex) => {
    onChange(true);
    if (subConsentDetails.length > 1) {
      const newFields = [...subConsentDetails];
      newFields.splice(fieldIndex, 1);
      setSubConsentDetails(newFields);
      // Remove error for the deleted field
      const newErrors = [...errors];
      newErrors.splice(fieldIndex, 1);
      setErrors(newErrors);
    }
  };

  const handleInputChange = (event, fieldIndex) => {
    const { value } = event.target;

    // Validate max length of 35 characters
    if (value.length > 35) {
      const newErrors = [...errors];
      newErrors[fieldIndex] = failureMessageConstant.NAME_TOO_LONG;
      setErrors(newErrors);
      return; // Don't update state if validation fails
    }

    const newFields = [...subConsentDetails];
    newFields[fieldIndex].name = value;
    setSubConsentDetails(newFields);
    onChange(true);
    const isDuplicate = newFields.some(
      (subConsent, index) =>
        subConsent.name.trim() !== "" &&
        index !== fieldIndex &&
        subConsent.name === value
    );

    // Set error if duplicate is found
    const newErrors = [...errors];
    newErrors[fieldIndex] = isDuplicate
      ? failureMessageConstant.NAME_MUST_BE_UNIQUE
      : "";
    setErrors(newErrors);
  };

  const setEmpty = (fieldIndex) => {
    if (!subConsentDetails[fieldIndex].name) {
      return;
    }
    onChange(true);
    const newFields = [...subConsentDetails];
    newFields[fieldIndex].name = "";
    setSubConsentDetails(newFields);
  };

  return (
    <div className="">
      <h2 className="mb-2 font-MontserratBold text-ft15-26.25 text-black-20">
        Create Sub-Consent
      </h2>
      {subConsentDetails.map((field, fieldIndex) => (
        <div className="mb-2 flex flex-col" key={fieldIndex + field._id}>
          <div className="flex">
            <input
              type="text"
              className="mr-4.5 w-89 rounded-md border border-grey-C7C7C7 px-13px py-15px font-MontserratMedium text-ft14-17.02-0.28 text-black-20 outline-none"
              value={field.name}
              onChange={(event) => handleInputChange(event, fieldIndex)}
            />
            {fieldIndex < 9 && (
              <button
                onClick={() => handleAddField(fieldIndex)}
                disabled={subConsentDetails.length >= 10}
              >
                <img
                  className="mr-2.5"
                  src="/images/plus-purple-circular.svg"
                  alt="Add icon"
                />
              </button>
            )}

            <button
              onClick={
                fieldIndex >= 1
                  ? () => handleRemoveField(fieldIndex)
                  : () => setEmpty(fieldIndex)
              }
            >
              <img src="/images/minus-purple.svg" alt="Subtract icon" />
            </button>
          </div>
          {errors[fieldIndex] && (
            <p className="mt-1 text-ft15 text-red-10">{errors[fieldIndex]}</p>
          )}
        </div>
      ))}
      <button
        className="mt-4 cursor-pointer font-MontserratMedium text-ft15-26.25 text-purple-6C36B0"
        onClick={toggleVisibility}
      >
        Cancel
      </button>
    </div>
  );
};

SubConsent.propTypes = {
  consentIndex: PropTypes.number,
  subConsentDetails: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
      name: PropTypes.string,
      isActive: PropTypes.bool,
      isDeleted: PropTypes.bool,
      addedOn: PropTypes.number,
      updatedOn: PropTypes.number,
      businessId: PropTypes.string,
      consentId: PropTypes.string
    })
  ).isRequired,
  setSubConsentDetails: PropTypes.func,
  toggleVisibility: PropTypes.func,
  onChange: PropTypes.func
};

export default SubConsent;
